import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, FormGroup, ControlLabel, Tabs, Tab } from 'react-bootstrap';
import Utils from '../util/Utils.jsx';
import { useStateValue } from '../context/state.jsx';
import { setProjectActivities } from '../context/datasets.js';
import { analyzeSchedule, analyzeRepository, generateScheduleNarrative } from '../util/ai/analyze.js';
import { NARRATIVE_TASK } from '../util/ai/prompts.js';
import { datasetDataDateMs, datasetIdOf, datasetName, priorDatasetOptions, priorSelectionHint, scheduleFamilyKey, suggestPriorDataset } from '../util/ai/suggestPriorDataset.js';
import {
	activityToAiTask,
	extractTaskCodes,
	isHitoEmail,
	matchTaskCodes,
	notesFromActivities,
	pickLowestFloatActivities
} from '../util/ai/serializeTask.js';
import { calculateDcmaMetrics } from '../util/ai/dcmaMetrics.js';
import { formatNarrativeProgress, localNarrativeStatus, localPreloadStatus, narrativeDebugText, preloadProxyFailure } from '../util/ai/narrativeProgress.js';

const AUDIENCES = ['Foreman', 'Project Manager', 'Executive Stakeholder'];
const DELAY_REVIEW_SECTIONS = [
	'delay_finish_movement',
	'delay_driving_path',
	'delay_duration_growth',
	'delay_late_actuals',
	'delay_remaining_duration',
	'delay_logic_changes',
	'delay_constraints',
	'delay_calendar',
	'delay_progress_lag',
	'delay_float_consumption',
	'delay_svp_oos'
];
const TIA_REVIEW_SECTIONS = [
	'tia_unimpacted_finish',
	'tia_impacted_finish',
	'tia_milestone_impact',
	'tia_driving_path',
	'tia_float_consumption',
	'tia_fragnet',
	'tia_zero_duration_check'
];
const TIA_REL_TYPES = ['PR_FS', 'PR_SS', 'PR_FF', 'PR_SF'];
const WEEKLY_REVIEW_SECTIONS = ['slips', 'finals', 'critical', 'earned_value', 'next_week', 'dates', 'progress', 'constraints', 'relationships', 'health'];
const BASIS_REVIEW_SECTIONS = [
	'basis_project',
	'basis_milestones',
	'basis_calendars',
	'basis_wbs',
	'basis_critical',
	'basis_float',
	'basis_turnover',
	'basis_resources',
	'basis_reserve',
	'basis_codes',
	'basis_options',
	'basis_quality',
	'basis_software',
	'slips',
	'new_activities',
	'activities_added_deleted'
];
const BASIS_FIELDS = [
	{ key: 'scope', label: 'Scope of work' },
	{ key: 'execution', label: 'Execution and procurement strategy' },
	{ key: 'planning', label: 'Planning basis (access, crews, how durations were set)' },
	{ key: 'path', label: 'Path of execution (how crews move through the work)' },
	{ key: 'issues', label: 'Issues and concerns' },
	{ key: 'risks', label: 'Risks and opportunities' },
	{ key: 'assumptions', label: 'Assumptions' },
	{ key: 'exclusions', label: 'Exclusions' },
	{ key: 'exceptions', label: 'Exceptions' }
];

function blankFragnet() {
	return {
		code: '',
		name: '',
		hours: '16',
		calendarId: '',
		pred: '',
		predType: 'PR_FS',
		predLag: '0',
		succ: '',
		succType: 'PR_FS',
		succLag: '0'
	};
}

function fragnetPayload(rows, changeCode, changeHours, changeConstraint, changeDate) {
	const activities = [];
	const relationships = [];
	rows.forEach(row => {
		const code = String(row.code || '').trim();
		if (!code) return;
		activities.push({
			code,
			name: String(row.name || code).trim(),
			durationHours: row.hours === '' ? 0 : Number(row.hours),
			calendarId: String(row.calendarId || '').trim()
		});
		if (String(row.pred || '').trim()) {
			relationships.push({
				pred: String(row.pred).trim(),
				succ: code,
				type: row.predType || 'PR_FS',
				lagHours: row.predLag === '' ? 0 : Number(row.predLag)
			});
		}
		if (String(row.succ || '').trim()) {
			relationships.push({
				pred: code,
				succ: String(row.succ).trim(),
				type: row.succType || 'PR_FS',
				lagHours: row.succLag === '' ? 0 : Number(row.succLag)
			});
		}
	});
	const changes = [];
	if (String(changeCode || '').trim()) {
		const change = { code: String(changeCode).trim() };
		if (changeHours !== '') change.remainHours = Number(changeHours);
		if (changeConstraint) {
			change.constraintType = changeConstraint;
			change.constraintDate = changeDate || '';
		}
		changes.push(change);
	}
	return { activities, relationships, changes };
}

function delayChainIds (datasets, datasetInfo, datasetId, priorId, span) {
	const currentId = String(datasetId || datasetIdOf(datasetInfo) || '');
	const prior = String(priorId || '');
	if (!span || !prior || !datasets) {
		return [prior, currentId].filter(Boolean).join(',');
	}
	const family = scheduleFamilyKey(datasetName(datasetInfo));
	const priorInfo = Object.keys(datasets).map(id => datasets[id]).find(item => datasetIdOf(item) === prior);
	const from = datasetDataDateMs(priorInfo);
	const to = datasetDataDateMs(datasetInfo);
	const rows = Object.keys(datasets).map(id => datasets[id]).filter(item => {
		if (!item) return false;
		if (scheduleFamilyKey(datasetName(item)) !== family) return false;
		const id = datasetIdOf(item);
		if (id === prior || id === currentId) return true;
		const ms = datasetDataDateMs(item);
		if (!from || !to || !ms) return false;
		return ms >= from && ms <= to;
	});
	rows.sort((a, b) => datasetDataDateMs(a) - datasetDataDateMs(b));
	const ids = [];
	rows.forEach(item => {
		const id = datasetIdOf(item);
		if (id && ids.indexOf(id) === -1) ids.push(id);
	});
	if (prior && ids.indexOf(prior) === -1) ids.unshift(prior);
	if (currentId && ids.indexOf(currentId) === -1) ids.push(currentId);
	return ids.join(',');
}
const ACTIVITY_CAP = 40;
const COMPLETE_OPTS = { endpoint: 'aiComplete' };

const AiInsightsPanel = ({ datasetId }) => {
	const [{
		emailAddress,
		datasets,
		filteredActivities,
		projectActivities,
		projectActivitiesMap
	}, dispatch] = useStateValue();

	const [open, setOpen] = useState(false);
	const [tab, setTab] = useState(1);
	const [health, setHealth] = useState(null);
	const [provider, setProvider] = useState('local');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [riskText, setRiskText] = useState('');
	const [updatesText, setUpdatesText] = useState('');
	const [narrativeText, setNarrativeText] = useState('');
	const [narrativeMode, setNarrativeMode] = useState('weekly');
	const [basisNotes, setBasisNotes] = useState({
		scope: '',
		execution: '',
		planning: '',
		path: '',
		issues: '',
		risks: '',
		assumptions: '',
		exclusions: '',
		exceptions: ''
	});
	const [basisFacts, setBasisFacts] = useState('');
	const [basisDocx, setBasisDocx] = useState(null);
	const [basisDocxError, setBasisDocxError] = useState('');
	const [basisExplain, setBasisExplain] = useState(true);
	const [narrativeScope, setNarrativeScope] = useState(NARRATIVE_TASK);
	const [audience, setAudience] = useState('Project Manager');
	const [useTemplate, setUseTemplate] = useState(false);
	const [priorId, setPriorId] = useState('');
	const [emailOn, setEmailOn] = useState(false);
	const [sourcesOn, setSourcesOn] = useState(false);
	const [fullReviewOn, setFullReviewOn] = useState(false);
	const [debugOn, setDebugOn] = useState(false);
	const [runId, setRunId] = useState(0);
	const [reviewSection, setReviewSection] = useState('slips');
	const [reviewComment, setReviewComment] = useState('');
	const [reviewEdit, setReviewEdit] = useState('');
	const [reviewNote, setReviewNote] = useState('');
	const [debugInfo, setDebugInfo] = useState(null);
	const [delayRows, setDelayRows] = useState([]);
	const [delayWindows, setDelayWindows] = useState([]);
	const [delayText, setDelayText] = useState('');
	const [delayMethod, setDelayMethod] = useState('');
	const [delayLimits, setDelayLimits] = useState('');
	const [delayNote, setDelayNote] = useState('');
	const [delayFacts, setDelayFacts] = useState('');
	const [delayRunId, setDelayRunId] = useState(0);
	const [delayExplain, setDelayExplain] = useState(true);
	const [delaySpan, setDelaySpan] = useState(true);
	const [delayDebug, setDelayDebug] = useState(false);
	const [delaySection, setDelaySection] = useState('delay_finish_movement');
	const [delayComment, setDelayComment] = useState('');
	const [delayEdit, setDelayEdit] = useState('');
	const [delayReviewNote, setDelayReviewNote] = useState('');
	const [delayMode, setDelayMode] = useState('windows');
	const [tiaEventDate, setTiaEventDate] = useState('');
	const [tiaName, setTiaName] = useState('');
	const [tiaAssumptions, setTiaAssumptions] = useState('');
	const [tiaExplain, setTiaExplain] = useState(true);
	const [tiaDebug, setTiaDebug] = useState(false);
	const [tiaActivities, setTiaActivities] = useState([blankFragnet()]);
	const [tiaChangeCode, setTiaChangeCode] = useState('');
	const [tiaChangeHours, setTiaChangeHours] = useState('');
	const [tiaChangeConstraint, setTiaChangeConstraint] = useState('');
	const [tiaChangeDate, setTiaChangeDate] = useState('');
	const [tiaRows, setTiaRows] = useState([]);
	const [tiaText, setTiaText] = useState('');
	const [tiaMethod, setTiaMethod] = useState('');
	const [tiaLimits, setTiaLimits] = useState('');
	const [tiaNote, setTiaNote] = useState('');
	const [tiaFacts, setTiaFacts] = useState('');
	const [tiaWarnings, setTiaWarnings] = useState([]);
	const [tiaRunId, setTiaRunId] = useState(0);
	const [tiaFragnet, setTiaFragnet] = useState(null);
	const [tiaSummary, setTiaSummary] = useState(null);
	const [tiaScenarioId, setTiaScenarioId] = useState(0);
	const [tiaParity, setTiaParity] = useState('');
	const [tiaMismatches, setTiaMismatches] = useState([]);
	const [tiaScenarioNote, setTiaScenarioNote] = useState('');
	const [tiaSection, setTiaSection] = useState('tia_impacted_finish');
	const [tiaComment, setTiaComment] = useState('');
	const [tiaEdit, setTiaEdit] = useState('');
	const [tiaReviewNote, setTiaReviewNote] = useState('');
	const [narrativeNote, setNarrativeNote] = useState('');
	const [narrativeProgress, setNarrativeProgress] = useState('');
	const [codeWarning, setCodeWarning] = useState('');
	const [truncationWarning, setTruncationWarning] = useState('');
	const [preload, setPreload] = useState(null);
	const [preloadClock, setPreloadClock] = useState(0);
	const [basisChapters, setBasisChapters] = useState([]);
	const basisJobId = useRef('');
	const [flagMessage, setFlagMessage] = useState('');
	const preloadOnce = useRef('');

	const allowed = isHitoEmail(emailAddress);
	const datasetInfo = datasetId && datasets ? datasets[datasetId] : null;

	const loadHealth = useCallback(async () => {
		try {
			const result = await Utils.ajaxAsync('aiHealth', {}, dispatch, { suppressErrorAlert: true });
			setHealth(result);
			if (result && (result.preferredProvider === 'gemini' || result.preferredProvider === 'local')) {
				setProvider(result.preferredProvider);
			}
		} catch (e) {
			setHealth({ success: false, configured: false, reachable: false, error: e.message });
		}
	}, [dispatch]);

	useEffect(() => {
		if (open && allowed) loadHealth();
	}, [open, allowed, loadHealth]);

	useEffect(() => {
		if (!open) {
			preloadOnce.current = '';
			setPreload(null);
			return undefined;
		}
		if (!allowed || !health || !health.localConfigured || health.preloadOnOpen === false) return undefined;
		if (preloadOnce.current === 'open') return undefined;
		preloadOnce.current = 'open';
		const model = health.narrativeModel || health.model || '';
		const startedAt = Date.now() / 1000;
		const alreadyLoaded = health.loadState === 'loaded' || health.narrativeModelLoaded === true;
		if (!alreadyLoaded) {
			setPreload({ phase: 'loading', state: 'loading', model, startedAt });
		}
		let cancelled = false;
		let timer = null;
		const stop = () => {
			if (timer) clearTimeout(timer);
			timer = null;
		};
		const schedule = () => {
			stop();
			timer = setTimeout(() => {
				tick('aiPreloadStatus');
			}, 3000);
		};
		const keepWaiting = (message) => {
			if (cancelled) return;
			if (!preloadProxyFailure(message)) {
				setPreload({ phase: 'error', state: 'error', error: message || 'Model load failed', model });
				return;
			}
			setPreload(prev => ({
				...(prev || {}),
				phase: 'loading',
				state: 'loading',
				model,
				startedAt: (prev && prev.startedAt) || startedAt
			}));
			schedule();
		};
		const tick = (endpoint) => {
			Utils.ajaxAsync(endpoint, {}, dispatch, { suppressErrorAlert: true })
				.then(result => {
					if (cancelled) return;
					if (preloadProxyFailure(result) || preloadProxyFailure(result && result.error)) {
						keepWaiting((result && result.error) || 'Gateway Time-out');
						return;
					}
					const next = result || {};
					const phase = next.phase || (next.state === 'ready' ? 'done' : next.state) || 'error';
					setPreload({ ...next, phase, model: next.model || model });
					if (phase === 'done' || next.loadState === 'loaded' || next.alreadyLoaded) {
						setHealth(prev => {
							if (!prev || (prev.loadState === 'loaded' && prev.narrativeModelLoaded === true)) return prev;
							return {
								...prev,
								loadState: 'loaded',
								narrativeModelLoaded: true,
								narrativeModel: next.model || prev.narrativeModel
							};
						});
					}
					if (phase === 'loading' || next.state === 'loading') schedule();
				})
				.catch(e => keepWaiting(e && e.message));
		};
		tick('aiPreload');
		schedule();
		return () => {
			cancelled = true;
			stop();
		};
	}, [open, allowed, health, dispatch]);

	useEffect(() => {
		const loading = preload && (preload.phase === 'loading' || preload.state === 'loading');
		if (!loading) return undefined;
		setPreloadClock(Date.now());
		const id = setInterval(() => setPreloadClock(Date.now()), 1000);
		return () => clearInterval(id);
	}, [preload]);

	const sourceActivities = useMemo(() => {
		if (filteredActivities && filteredActivities.length) return filteredActivities;
		return projectActivities || [];
	}, [filteredActivities, projectActivities]);

	const aiTasks = useMemo(() => {
		const rows = provider === 'gemini'
			? (sourceActivities || []).filter(Boolean)
			: pickLowestFloatActivities(sourceActivities, ACTIVITY_CAP);
		return rows.map(activity => activityToAiTask(activity)).filter(Boolean);
	}, [sourceActivities, provider]);

	const fieldNotes = useMemo(() => notesFromActivities(sourceActivities), [sourceActivities]);
	const suggestedPrior = useMemo(
		() => suggestPriorDataset(datasetInfo, datasets),
		[datasetInfo, datasets]
	);
	const priorOptions = useMemo(
		() => priorDatasetOptions(datasetInfo, datasets),
		[datasetInfo, datasets]
	);
	const priorHint = priorSelectionHint(priorId, suggestedPrior);
	useEffect(() => {
		setPriorId(suggestedPrior || '');
	}, [datasetId, suggestedPrior]);
	useEffect(() => {
		const ms = datasetDataDateMs(datasetInfo);
		if (!ms) return;
		const dt = new Date(ms);
		const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
		const day = String(dt.getUTCDate()).padStart(2, '0');
		setTiaEventDate(`${dt.getUTCFullYear()}-${month}-${day}`);
	}, [datasetId, datasetInfo]);
	const dcmaMetrics = useMemo(
		() => calculateDcmaMetrics(
			projectActivities && projectActivities.length ? projectActivities : sourceActivities,
			{
				dataDate: datasetDataDateMs(datasetInfo) || '',
				hoursPerDay: datasetInfo && datasetInfo.HoursPerDay,
				dataset: datasetInfo
			}
		),
		[projectActivities, sourceActivities, datasetInfo]
	);

	const currentText = tab === 1 ? riskText : tab === 2 ? updatesText : '';
	const suggested = useMemo(() => {
		if (!currentText) return { matched: [], unmatched: [] };
		return matchTaskCodes(extractTaskCodes(currentText), aiTasks);
	}, [currentText, aiTasks]);

	if (!allowed || !datasetId) return null;

	const localReady = health && health.localConfigured && health.reachable;
	const geminiReady = health && health.geminiConfigured;
	const selectedReady = provider === 'gemini' ? geminiReady : localReady;
	const localStatus = localPreloadStatus(health, preload, preloadClock || undefined) || localNarrativeStatus(health);
	const geminiStatus = !health
		? 'Checking…'
		: !health.cloudAllowed
			? 'Disabled (CLOUD_ALLOWED)'
			: health.geminiConfigured
				? `Ready (${health.geminiModel || 'Gemini'}${health.geminiMaxTokens ? `, ${health.geminiMaxTokens} tokens` : ''})`
				: 'Not configured';

	const completeOpts = { ...COMPLETE_OPTS, provider };

	const runRisk = async () => {
		setBusy(true);
		setError('');
		setRiskText('');
		setFlagMessage('');
		try {
			setRiskText(await analyzeSchedule(aiTasks, dispatch, completeOpts));
		} catch (e) {
			setError(e.message || 'AI request failed');
		} finally {
			setBusy(false);
		}
	};

	const runUpdates = async () => {
		setBusy(true);
		setError('');
		setUpdatesText('');
		setFlagMessage('');
		try {
			setUpdatesText(await analyzeRepository(aiTasks, fieldNotes, dispatch, completeOpts));
		} catch (e) {
			setError(e.message || 'AI request failed');
		} finally {
			setBusy(false);
		}
	};

	const applyNarrativeResult = (result) => {
		setDebugInfo(result && result.debug ? result.debug : null);
		const nextRun = result && (result.runId || (result.debug && result.debug.runId));
		setRunId(nextRun ? Number(nextRun) : 0);
		setReviewNote('');
		if (result && result.dryRun) {
			setNarrativeText('');
			setNarrativeNote('Dry run. The model was not called. The digest and prompt are below.');
			return;
		}
		if (!result || result.success === false || result.status === 'error') {
			const msg = (result && (result.error || result.hint)) || 'Narrative request failed';
			setError(msg);
			setNarrativeNote(msg);
			return;
		}
		if (!result.text || !String(result.text).trim()) {
			const msg = (result && result.error) || 'The model returned no narrative text.';
			setError(msg);
			setNarrativeNote(msg);
			return;
		}
		setNarrativeText(result.text);
		setCodeWarning((result && (result.codeWarning || (result.debug && result.debug.codeWarning))) || '');
		setTruncationWarning(truncationFrom(result));
		if (result.emailSent) {
			setNarrativeNote('Emailed the narrative to you.');
		} else if (emailOn && result.emailError) {
			setNarrativeNote(result.emailError);
		} else {
			setNarrativeNote('');
		}
	};

	const truncationFrom = (result) => {
		const finish = String((result && (result.finishReason || (result.debug && result.debug.finishReason))) || '');
		let truncation = (result && result.truncatedWarning) || '';
		if (!truncation && (finish === 'length' || finish === 'MAX_TOKENS' || finish === 'max_tokens')) {
			truncation = 'This email is incomplete. The model stopped because it reached the output limit. The text below is not a finished narrative.';
		}
		return truncation;
	};

	const runNarrative = async (dryRun) => {
		setBusy(true);
		setError('');
		setNarrativeText('');
		setNarrativeNote('');
		setCodeWarning('');
		setTruncationWarning('');
		setDebugInfo(null);
		setRunId(0);
		setReviewNote('');
		setNarrativeProgress('');
		try {
			const result = await generateScheduleNarrative({
				dataset: datasetId,
				priorDataset: priorId,
				provider,
				scope: narrativeScope,
				audience,
				fieldVoice: useTemplate ? 1 : '',
				email: !dryRun && emailOn ? 1 : '',
				sourcesFooter: sourcesOn ? 1 : '',
				fullReview: fullReviewOn ? 1 : '',
				debug: debugOn || dryRun ? 1 : '',
				dryRun: dryRun ? 1 : ''
			}, dispatch, (status) => setNarrativeProgress(formatNarrativeProgress(status)));
			applyNarrativeResult(result);
		} catch (e) {
			setError(e.message || 'AI request failed');
		} finally {
			setBusy(false);
			setNarrativeProgress('');
		}
	};

	const chapterPreview = (chapters) => {
		if (!Array.isArray(chapters)) return '';
		return chapters.map(chapter => {
			const body = chapter && chapter.text ? String(chapter.text).trim() : '';
			if (!body) return '';
			const title = chapter && chapter.title ? String(chapter.title).trim() : '';
			return title ? `${title}\n${body}` : body;
		}).filter(Boolean).join('\n\n');
	};

	const applyBasisResult = (result) => {
		if (!result || result.success === false || result.status === 'error') {
			setError((result && (result.error || result.hint)) || 'Schedule basis failed');
			return;
		}
		setNarrativeText(result.text || '');
		setBasisFacts(result.factsText || '');
		setBasisDocx(result.docx || null);
		setBasisDocxError(result.docxError || '');
		setCodeWarning(result.codeWarning || '');
		setTruncationWarning(truncationFrom(result));
		setRunId(result.runId ? Number(result.runId) : 0);
		basisJobId.current = result.jobId || '';
		setBasisChapters(Array.isArray(result.chapters) ? result.chapters : []);
		setNarrativeNote(result.modelError || result.notice || '');
		if (result.text) {
			const modelName = result.model || (health && (health.narrativeModel || health.model)) || '';
			setHealth(prev => (prev ? {
				...prev,
				loadState: 'loaded',
				narrativeModelLoaded: true,
				narrativeModel: prev.narrativeModel || modelName
			} : prev));
			setPreload({
				phase: 'done',
				state: 'ready',
				model: modelName,
				alreadyLoaded: true
			});
		}
	};

	const runBasis = async (retryChapter) => {
		const retryId = typeof retryChapter === 'string' ? retryChapter : '';
		setBusy(true);
		setError('');
		setNarrativeNote('');
		setCodeWarning('');
		setTruncationWarning('');
		setReviewNote('');
		setNarrativeProgress('');
		if (!retryId) {
			setNarrativeText('');
			setBasisFacts('');
			setBasisDocx(null);
			setBasisDocxError('');
			setRunId(0);
			basisJobId.current = '';
			setBasisChapters([]);
		}
		try {
			const result = await generateScheduleNarrative({
				dataset: datasetId,
				priorDataset: priorId,
				explain: basisExplain || retryId ? 1 : 0,
				debug: debugOn ? 1 : '',
				retryChapter: retryId,
				sourceJobId: retryId ? basisJobId.current : '',
				scope: basisNotes.scope,
				execution: basisNotes.execution,
				planning: basisNotes.planning,
				path: basisNotes.path,
				issues: basisNotes.issues,
				risks: basisNotes.risks,
				assumptions: basisNotes.assumptions,
				exclusions: basisNotes.exclusions,
				exceptions: basisNotes.exceptions
			}, dispatch, (status) => {
				setNarrativeProgress(formatNarrativeProgress(status));
				const list = (status && status.progress && status.progress.chapters) || (status && status.chapters) || [];
				if (list.length) {
					setBasisChapters(list);
					if (!status || status.status !== 'done') {
						const preview = chapterPreview(list);
						if (preview) setNarrativeText(preview);
					}
				}
			}, { start: 'basisNarrative' });
			applyBasisResult(result);
		} catch (e) {
			setError(e.message || 'Schedule basis failed');
		} finally {
			setBusy(false);
			setNarrativeProgress('');
		}
	};

	const runDelay = async () => {
		if (!priorId) {
			setError('Pick a prior update to measure the delay.');
			return;
		}
		setBusy(true);
		setError('');
		setDelayRows([]);
		setDelayWindows([]);
		setDelayText('');
		setDelayMethod('');
		setDelayLimits('');
		setDelayNote('');
		setCodeWarning('');
		setTruncationWarning('');
		setDelayFacts('');
		setDelayRunId(0);
		setDelayReviewNote('');
		setNarrativeProgress('');
		try {
			const result = await generateScheduleNarrative({
				dataset: datasetId,
				priorDataset: priorId,
				chain: delayChainIds(datasets, datasetInfo, datasetId, priorId, delaySpan),
				explain: delayExplain ? 1 : 0,
				debug: delayDebug ? 1 : ''
			}, dispatch, (status) => setNarrativeProgress(formatNarrativeProgress(status)), { start: 'delayAnalysis' });
			if (!result || result.success === false) {
				setError((result && result.error) || 'Delay analysis failed');
				return;
			}
			setDelayRows(Array.isArray(result.rows) ? result.rows : []);
			setDelayWindows(Array.isArray(result.windows) ? result.windows : []);
			setDelayText(result.text || '');
			setDelayMethod(result.method || '');
			setDelayLimits(result.limits || '');
			setDelayFacts(result.factsText || '');
			setDelayRunId(result.runId ? Number(result.runId) : 0);
			setDelayNote(result.modelError || '');
			setCodeWarning(result.codeWarning || '');
			setTruncationWarning(truncationFrom(result));
		} catch (e) {
			setError(e.message || 'Delay analysis failed');
		} finally {
			setBusy(false);
			setNarrativeProgress('');
		}
	};

	const submitDelayReview = async (status) => {
		if (!delayRunId) return;
		setDelayReviewNote('');
		try {
			const result = await Utils.ajaxAsync('narrativeReview', {
				runId: delayRunId,
				status,
				section: delaySection,
				queryKey: delaySection,
				comment: delayComment,
				editedText: delayEdit
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setDelayReviewNote((result && result.error) || 'The review was not saved.');
				return;
			}
			const query = result.review && result.review.queryKey
				? ` (${result.review.queryKey}@${result.review.queryVersion || 1})`
				: '';
			setDelayReviewNote(status === 'approved' ? 'Approved.' : `Flagged${query}.`);
		} catch (e) {
			setDelayReviewNote(e.message || 'The review was not saved.');
		}
	};

	const updateFragnet = (index, field, value) => {
		setTiaActivities(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
	};

	const runTia = async () => {
		const fragnet = fragnetPayload(tiaActivities, tiaChangeCode, tiaChangeHours, tiaChangeConstraint, tiaChangeDate);
		if (!tiaEventDate) {
			setError('Enter the date the event starts.');
			return;
		}
		if (!fragnet.activities.length || !fragnet.relationships.length) {
			setError('Add a fragnet activity and tie it to an existing activity.');
			return;
		}
		setBusy(true);
		setError('');
		setTiaRows([]);
		setTiaText('');
		setTiaMethod('');
		setTiaLimits('');
		setTiaNote('');
		setCodeWarning('');
		setTruncationWarning('');
		setTiaFacts('');
		setTiaWarnings([]);
		setTiaParity('');
		setTiaMismatches([]);
		setTiaRunId(0);
		setTiaFragnet(null);
		setTiaSummary(null);
		setTiaScenarioId(0);
		setTiaScenarioNote('');
		setTiaReviewNote('');
		setNarrativeProgress('');
		try {
			const result = await generateScheduleNarrative({
				dataset: datasetId,
				eventDate: tiaEventDate,
				name: tiaName,
				assumptions: tiaAssumptions,
				fragnet: JSON.stringify(fragnet),
				explain: tiaExplain ? 1 : 0
			}, dispatch, (status) => setNarrativeProgress(formatNarrativeProgress(status)), { start: 'tiaAnalysis' });
			if (!result || result.success === false) {
				setTiaParity((result && result.parity) || '');
				setTiaMismatches(Array.isArray(result && result.dateMismatches) ? result.dateMismatches : []);
				setError((result && result.error) || 'Time impact failed');
				return;
			}
			setTiaRows(Array.isArray(result.rows) ? result.rows : []);
			setTiaText(result.text || '');
			setTiaMethod(result.method || '');
			setTiaLimits(result.limits || '');
			setTiaFacts(result.factsText || '');
			setTiaWarnings(Array.isArray(result.warnings) ? result.warnings : []);
			setTiaParity(result.parity || '');
			setTiaMismatches(Array.isArray(result.dateMismatches) ? result.dateMismatches : []);
			setTiaRunId(result.runId ? Number(result.runId) : 0);
			setTiaFragnet(result.fragnet || fragnet);
			setTiaSummary({
				unimpactedFinish: result.unimpactedFinish,
				impactedFinish: result.impactedFinish,
				impactCalendarDays: result.impactCalendarDays,
				impactWorkHours: result.impactWorkHours,
				zeroDurationMatch: result.zeroDurationMatch,
				warnings: result.warnings || [],
				drivingPath: result.drivingPath || [],
				method: result.method || ''
			});
			setTiaNote(result.modelError || '');
			setCodeWarning(result.codeWarning || '');
			setTruncationWarning(truncationFrom(result));
		} catch (e) {
			setError(e.message || 'Time impact failed');
		} finally {
			setBusy(false);
			setNarrativeProgress('');
		}
	};

	const saveTia = async () => {
		if (!tiaSummary) return;
		setTiaScenarioNote('');
		try {
			const result = await Utils.ajaxAsync('tiaScenario', {
				action: 'save',
				dataset: datasetId,
				name: tiaName || 'Time impact',
				eventDate: tiaEventDate,
				assumptions: tiaAssumptions,
				runId: tiaRunId,
				fragnet: JSON.stringify(tiaFragnet || {}),
				result: JSON.stringify(tiaSummary)
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setTiaScenarioNote((result && result.error) || 'The scenario was not saved.');
				return;
			}
			setTiaScenarioId(result.scenario && result.scenario.id ? Number(result.scenario.id) : 0);
			setTiaScenarioNote('Saved as a draft.');
		} catch (e) {
			setTiaScenarioNote(e.message || 'The scenario was not saved.');
		}
	};

	const submitTiaReview = async (status) => {
		setTiaReviewNote('');
		try {
			if (tiaScenarioId) {
				const scenario = await Utils.ajaxAsync('tiaScenario', {
					action: 'status',
					scenarioId: tiaScenarioId,
					status
				}, dispatch, { suppressErrorAlert: true });
				if (!scenario || scenario.success === false) {
					setTiaReviewNote((scenario && scenario.error) || 'The scenario status was not saved.');
					return;
				}
			}
			if (!tiaRunId) {
				setTiaReviewNote(status === 'approved' ? 'Approved.' : 'Flagged.');
				return;
			}
			const result = await Utils.ajaxAsync('narrativeReview', {
				runId: tiaRunId,
				status,
				section: tiaSection,
				queryKey: tiaSection,
				comment: tiaComment,
				editedText: tiaEdit
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setTiaReviewNote((result && result.error) || 'The review was not saved.');
				return;
			}
			const query = result.review && result.review.queryKey
				? ` (${result.review.queryKey}@${result.review.queryVersion || 1})`
				: '';
			setTiaReviewNote(status === 'approved' ? 'Approved.' : `Flagged${query}.`);
		} catch (e) {
			setTiaReviewNote(e.message || 'The review was not saved.');
		}
	};

	const submitReview = async (status) => {
		if (!runId) return;
		setReviewNote('');
		try {
			const result = await Utils.ajaxAsync('narrativeReview', {
				runId,
				status,
				section: reviewSection,
				queryKey: narrativeMode === 'basis' ? reviewSection : '',
				comment: reviewComment,
				editedText: reviewEdit
			}, dispatch, { suppressErrorAlert: true });
			if (!result || result.success === false) {
				setReviewNote((result && result.error) || 'The review was not saved.');
				return;
			}
			const query = result.review && result.review.queryKey
				? ` (${result.review.queryKey}@${result.review.queryVersion || 1})`
				: '';
			setReviewNote(status === 'approved' ? 'Approved.' : `Flagged${query}.`);
		} catch (e) {
			setReviewNote(e.message || 'The review was not saved.');
		}
	};

	const applyFlags = (label) => {
		if (!suggested.matched.length || !projectActivities || !projectActivitiesMap) return;
		const flags = datasetInfo && datasetInfo.filterOptions ? datasetInfo.filterOptions.flags : [];
		const flag = flags.length ? flags[0].Code : null;
		if (!flag) {
			setFlagMessage('No project flags are available to apply.');
			return;
		}
		const ids = [];
		const byCode = {};
		(projectActivities || []).forEach(activity => {
			if (activity && activity.taskCode) {
				byCode[String(activity.taskCode).toLowerCase()] = activity.activityId;
			}
		});
		suggested.matched.forEach(code => {
			const id = byCode[String(code).toLowerCase()];
			if (id != null) ids.push(id);
		});
		if (!ids.length) {
			setFlagMessage('Matched IDs were not found in this project.');
			return;
		}
		const projectActivitiesTemp = [...projectActivities];
		Utils.ajax(
			'setActivityFlag',
			{ dataset: datasetId, activities: ids, flag },
			dispatch,
			() => {
				ids.forEach(activityId => {
					const activity = projectActivitiesTemp[projectActivitiesMap[activityId]];
					if (activity) {
						activity.flag = flag;
						activity.hasChanged = true;
					}
				});
				dispatch(setProjectActivities(projectActivitiesTemp));
				setFlagMessage(`${label}: flagged ${ids.length} ${ids.length === 1 ? 'activity' : 'activities'} (${suggested.matched.join(', ')}).`);
			}
		);
	};

	const copyNarrative = () => {
		if (!narrativeText || !navigator.clipboard) return;
		navigator.clipboard.writeText(narrativeText);
	};

	const downloadNarrative = () => {
		if (!narrativeText) return;
		const blob = new Blob([narrativeText], { type: 'text/plain;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'sitewalker-narrative.txt';
		a.click();
		URL.revokeObjectURL(url);
	};

	const downloadBasis = () => {
		if (!basisDocx || !basisDocx.body) return;
		const binary = atob(basisDocx.body);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
		const blob = new Blob([bytes], {
			type: basisDocx.mime || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = basisDocx.filename || 'schedule-basis.docx';
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<div className="aiInsightsRoot">
			{open
				? (
					<div className="aiInsightsPanel" role="dialog" aria-label="AI Insights">
						<div className="aiInsightsHeader">
							<div>
								<div className="aiInsightsTitle">AI Insights</div>
								<div className="aiInsightsSub">
									{aiTasks.length} of {sourceActivities.length} activities
									{provider !== 'gemini' && sourceActivities.length > ACTIVITY_CAP
										? ` (lowest-float cap ${ACTIVITY_CAP})`
										: ''}
									{' · '}{fieldNotes.length} field notes
								</div>
							</div>
							<Button bsSize="xsmall" onClick={() => setOpen(false)} aria-label="Close AI Insights">×</Button>
						</div>

						<FormGroup className="aiInsightsProvider">
							<ControlLabel>Model</ControlLabel>
							<div className="aiInsightsSwitch">
								<label className="radio-inline">
									<input
										type="radio"
										name="fieldAiProvider"
										checked={provider === 'local'}
										disabled={busy}
										onChange={() => setProvider('local')}
									/>
									{' '}Local LLM
								</label>
								<label className="radio-inline">
									<input
										type="radio"
										name="fieldAiProvider"
										checked={provider === 'gemini'}
										disabled={busy}
										onChange={() => setProvider('gemini')}
									/>
									{' '}Gemini
								</label>
							</div>
							<p className="aiInsightsMeta">Local: {localStatus}</p>
							<p className="aiInsightsMeta">Gemini: {geminiStatus}</p>
						</FormGroup>

						<Tabs
							id="ai-insights-tabs"
							activeKey={tab}
							onSelect={key => setTab(Number(key))}
							animation={false}>
							<Tab eventKey={1} title="Risk">
								<p className="aiInsightsHelp">Critical path, stacking, logic gaps, and out-of-sequence work.</p>
								<Button bsStyle="primary" bsSize="small" disabled={busy || !selectedReady || !aiTasks.length} onClick={runRisk}>
									{busy && tab === 1 ? 'Analyzing…' : 'Analyze risks'}
								</Button>
								{' '}
								<Button bsSize="small" disabled={busy || !suggested.matched.length || tab !== 1} onClick={() => applyFlags('Risk')}>
									Flag risks
								</Button>
								{riskText ? <pre className="aiInsightsOutput">{riskText}</pre> : null}
							</Tab>
							<Tab eventKey={2} title="Field update">
								<p className="aiInsightsHelp">Map superintendent notes to schedule updates.</p>
								<Button bsStyle="primary" bsSize="small" disabled={busy || !selectedReady || !aiTasks.length} onClick={runUpdates}>
									{busy && tab === 2 ? 'Analyzing…' : 'Analyze updates'}
								</Button>
								{' '}
								<Button bsSize="small" disabled={busy || !suggested.matched.length || tab !== 2} onClick={() => applyFlags('Updates')}>
									Flag updates
								</Button>
								{updatesText ? <pre className="aiInsightsOutput">{updatesText}</pre> : null}
							</Tab>
							<Tab eventKey={3} title="Metrics">
								<p className="aiInsightsHelp">
									DCMA assessment and schedule-health counts. Float and duration use each activity calendar&apos;s hours per day, and invalid dates use the schedule data date. Computed locally from the full loaded schedule (no model call).
								</p>
								{dcmaMetrics.length
									? dcmaMetrics.map(metric => (
										<div
											key={metric.id}
											className={`aiInsightsMetric aiInsightsMetric-${metric.status.toLowerCase()}`}>
											<div className="aiInsightsMetricHead">
												<strong>{metric.name}</strong>
												<span>{metric.status}</span>
											</div>
											<div className="aiInsightsMetricValue">
												{metric.percentage.toFixed(1)}%
												<span> ({metric.value}/{metric.total})</span>
											</div>
											<div className="aiInsightsMetricBar">
												<div style={{ width: `${Math.min(100, metric.percentage)}%` }} />
											</div>
											<div className="aiInsightsMetricFoot">
												<span>{metric.description}</span>
												<span>Goal: {metric.threshold}</span>
											</div>
										</div>
									))
									: <p className="aiInsightsMeta">No schedule data available for analysis.</p>}
							</Tab>
							<Tab eventKey={4} title="Narrative">
								<FormGroup>
									<ControlLabel>Narrative</ControlLabel>
									<FormControl
										componentClass="select"
										value={narrativeMode}
										onChange={e => {
											const mode = e.target.value;
											setNarrativeMode(mode);
											setReviewSection(mode === 'basis' ? 'basis_project' : 'slips');
										}}>
										<option value="weekly">Weekly update email</option>
										<option value="basis">Baseline narrative</option>
									</FormControl>
								</FormGroup>
								{narrativeMode === 'weekly' && (
									<p className="aiInsightsHelp">
										Compares this update to the prior week in code, then asks the model for an email of about 500 words.
									</p>
								)}
								{narrativeMode === 'basis' && (
									<p className="aiInsightsHelp">
										Schedule basis for this baseline or any single update, following the section outline of AACE RP 38R-06. The local model writes the email from the schedule facts and any notes you type. Schedule data is not sent out. The model does not invent assumptions, exclusions, or risks.
									</p>
								)}
								<FormGroup>
									<ControlLabel>{narrativeMode === 'basis' ? 'Prior baseline or update (for reconciliation)' : 'Prior weekly update'}</ControlLabel>
									<FormControl
										componentClass="select"
										value={priorId}
										onChange={e => setPriorId(e.target.value)}>
										<option value="">No prior update</option>
										{priorOptions.map(option => (
											<option key={option.id} value={option.id}>
												{option.sameProject ? '' : 'Other project — '}{option.name}
											</option>
										))}
									</FormControl>
									<p className="aiInsightsMeta">{priorHint.text}</p>
								</FormGroup>
								{narrativeMode === 'weekly' && (
									<div>
										<FormGroup>
											<ControlLabel>Request</ControlLabel>
											<FormControl
												componentClass="textarea"
												rows={6}
												value={narrativeScope}
												onChange={e => setNarrativeScope(e.target.value)}
											/>
										</FormGroup>
										<FormGroup>
											<ControlLabel>Audience</ControlLabel>
											<FormControl
												componentClass="select"
												value={audience}
												onChange={e => setAudience(e.target.value)}>
												{AUDIENCES.map(name => (
													<option key={name} value={name}>{name}</option>
												))}
											</FormControl>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={useTemplate}
													onChange={e => setUseTemplate(e.target.checked)}
												/>
												{' '}Use a plain field-update voice
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={emailOn}
													onChange={e => setEmailOn(e.target.checked)}
												/>
												{' '}Email me the narrative when it finishes
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={sourcesOn}
													onChange={e => setSourcesOn(e.target.checked)}
												/>
												{' '}Sources — append the query name and version to the email
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={fullReviewOn}
													onChange={e => setFullReviewOn(e.target.checked)}
												/>
												{' '}Attach the full update review (53R-06 check counts and lists)
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={debugOn}
													onChange={e => setDebugOn(e.target.checked)}
												/>
												{' '}Debug — show the digest, prompt, queries, model, tokens, and timing
											</label>
										</FormGroup>
										<Button bsStyle="primary" bsSize="small" disabled={busy || !selectedReady} onClick={() => runNarrative(false)}>
											{busy && tab === 4 ? 'Writing…' : 'Generate narrative'}
										</Button>
										{' '}
										<Button bsSize="small" disabled={busy} onClick={() => runNarrative(true)}>Dry run</Button>
										{' '}
										<Button bsSize="small" disabled={!narrativeText} onClick={copyNarrative}>Copy</Button>
										{' '}
										<Button bsSize="small" disabled={!narrativeText} onClick={downloadNarrative}>Download</Button>
									</div>
								)}
								{narrativeMode === 'basis' && (
									<div>
										{BASIS_FIELDS.map(field => (
											<FormGroup key={field.key}>
												<ControlLabel>{field.label}</ControlLabel>
												<FormControl
													componentClass="textarea"
													rows={2}
													value={basisNotes[field.key]}
													onChange={e => setBasisNotes(prev => ({ ...prev, [field.key]: e.target.value }))}
												/>
											</FormGroup>
										))}
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={basisExplain}
													onChange={e => setBasisExplain(e.target.checked)}
												/>
												{' '}Ask the local model for the email
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={debugOn}
													onChange={e => setDebugOn(e.target.checked)}
												/>
												{' '}Debug — show the schedule basis digest
											</label>
										</FormGroup>
										<Button bsStyle="primary" bsSize="small" disabled={busy} onClick={runBasis}>
											{busy && tab === 4 ? 'Writing…' : 'Generate basis'}
										</Button>
										{' '}
										<Button bsSize="small" disabled={!narrativeText} onClick={copyNarrative}>Copy email</Button>
										{' '}
										<Button bsSize="small" disabled={!basisDocx} onClick={downloadBasis}>Download DOCX</Button>
										{basisDocxError ? <p className="aiInsightsWarn">{basisDocxError}</p> : null}
									</div>
								)}
								{narrativeProgress ? <p className="aiInsightsMeta">{narrativeProgress}</p> : null}
								{narrativeMode === 'basis' && basisChapters.filter(chapter => chapter.status === 'error' || chapter.status === 'truncated').map(chapter => (
									<p key={chapter.id} className="aiInsightsMeta">
										{chapter.title}{' '}
										<Button bsSize="small" disabled={busy} onClick={() => runBasis(chapter.id)}>Retry chapter</Button>
									</p>
								))}
								{narrativeNote ? <p className="aiInsightsMeta">{narrativeNote}</p> : null}
								{narrativeText ? <pre className="aiInsightsOutput">{narrativeText}</pre> : null}
								{runId > 0 && (
									<div className="aiInsightsReview">
										<p className="aiInsightsMeta">Review this run. A flag records the query that produced the section.</p>
										<Button bsSize="small" bsStyle="success" disabled={busy} onClick={() => submitReview('approved')}>Approve</Button>
										{' '}
										<FormControl
											componentClass="select"
											value={reviewSection}
											onChange={e => setReviewSection(e.target.value)}
											style={{ display: 'inline-block', width: 'auto', marginLeft: 8 }}>
											{(narrativeMode === 'basis' ? BASIS_REVIEW_SECTIONS : WEEKLY_REVIEW_SECTIONS).map(name => (
												<option key={name} value={name}>{name}</option>
											))}
										</FormControl>
										<FormControl
											componentClass="textarea"
											rows={2}
											placeholder="Flag comment"
											value={reviewComment}
											onChange={e => setReviewComment(e.target.value)}
										/>
										<FormControl
											componentClass="textarea"
											rows={3}
											placeholder="Optional edited email text"
											value={reviewEdit}
											onChange={e => setReviewEdit(e.target.value)}
										/>
										<Button bsSize="small" disabled={busy} onClick={() => submitReview('flagged')}>Flag</Button>
										{reviewNote ? <p className="aiInsightsMeta">{reviewNote}</p> : null}
									</div>
								)}
								{truncationWarning ? <p className="aiInsightsWarn">{truncationWarning}</p> : null}
								{codeWarning ? <p className="aiInsightsWarn">{codeWarning}</p> : null}
								{debugInfo
									? (
										<pre className="aiInsightsDebug">
											{narrativeDebugText(debugInfo)}
										</pre>
									)
									: null}
								{narrativeMode === 'basis' && debugOn && basisFacts
									? <pre className="aiInsightsDebug">{basisFacts}</pre>
									: null}
							</Tab>
							<Tab eventKey={5} title="Delay">
								<FormGroup>
									<ControlLabel>Method</ControlLabel>
									<FormControl
										componentClass="select"
										value={delayMode}
										onChange={e => setDelayMode(e.target.value)}>
										<option value="windows">Windows — what already caused the delay</option>
										<option value="tia">Time impact — what a new event will do</option>
									</FormControl>
								</FormGroup>
								{delayMode === 'windows' && (
									<p className="aiInsightsHelp">
										What caused this delay? Compares consecutive updates in code, then asks the local model to explain the table. The schedule is not sent to an external service.
									</p>
								)}
								{delayMode === 'tia' && (
									<p className="aiInsightsHelp">
										Time impact uses the open update as the accepted schedule. The fragnet is calculated on a copy. The local model only explains the result.
									</p>
								)}
								{delayMode === 'windows' && (
									<div>
										<FormGroup>
											<ControlLabel>Prior weekly update</ControlLabel>
											<FormControl
												componentClass="select"
												value={priorId}
												onChange={e => setPriorId(e.target.value)}>
												<option value="">No prior update</option>
												{priorOptions.map(option => (
													<option key={option.id} value={option.id}>
														{option.sameProject ? '' : 'Other project — '}{option.name}
													</option>
												))}
											</FormControl>
											<p className="aiInsightsMeta">{priorHint.text}</p>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={delaySpan}
													onChange={e => setDelaySpan(e.target.checked)}
												/>
												{' '}Every update between the prior and this one
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={delayExplain}
													onChange={e => setDelayExplain(e.target.checked)}
												/>
												{' '}Explain the table with the local model
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input
													type="checkbox"
													checked={delayDebug}
													onChange={e => setDelayDebug(e.target.checked)}
												/>
												{' '}Debug — show the computed facts
											</label>
										</FormGroup>
										<Button bsStyle="primary" bsSize="small" disabled={busy || !priorId} onClick={runDelay}>
											{busy && tab === 5 ? 'Analyzing…' : 'What caused this delay?'}
										</Button>
										{narrativeProgress && delayMode === 'windows' ? <p className="aiInsightsMeta">{narrativeProgress}</p> : null}
										{delayMethod ? <p className="aiInsightsMeta">{delayMethod}</p> : null}
										{delayLimits ? <p className="aiInsightsMeta">{delayLimits}</p> : null}
										{delayNote ? <p className="aiInsightsMeta">{delayNote}</p> : null}
										{delayRows.length > 0 && (
											<div className="aiInsightsDelayWrap">
												<table className="aiInsightsDelayTable">
													<thead>
														<tr>
															<th>Window</th>
															<th>Finish movement</th>
															<th>Driving activity</th>
															<th>Cause</th>
															<th>Category</th>
															<th>Days</th>
															<th>Query</th>
														</tr>
													</thead>
													<tbody>
														{delayRows.map((row, index) => (
															<tr key={`${row.queryKey}-${row.activity}-${row.cause}-${index}`}>
																<td>{row.window}</td>
																<td>{row.finishMovement}</td>
																<td>{row.activity}</td>
																<td>{row.causeLabel || row.cause}</td>
																<td>{row.category}</td>
																<td>{row.days}</td>
																<td>{row.queryKey}@{row.queryVersion}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
										{delayWindows.map(item => (
											<p key={item.label} className="aiInsightsMeta">
												{item.label}
												{': '}
										SVP out-of-sequence {item.svp && item.svp.oos ? item.svp.oos.count : 'n/a'}
												{', '}
										actuals after data date {item.svp && item.svp.futureActuals ? item.svp.futureActuals.count : 'n/a'}
												{', '}
										open ends {item.svp && item.svp.openEnds ? item.svp.openEnds.count : 'n/a'}
												{', '}
										hard constraints {item.svp && item.svp.constraints ? item.svp.constraints.count : 'n/a'}
												{item.asBuiltCriticalPathUndetermined ? '. As-built critical path cannot be determined by CPM.' : ''}
											</p>
										))}
										{truncationWarning ? <p className="aiInsightsWarn">{truncationWarning}</p> : null}
										{codeWarning ? <p className="aiInsightsWarn">{codeWarning}</p> : null}
										{delayText ? <pre className="aiInsightsOutput">{delayText}</pre> : null}
										{delayRunId > 0 && (
											<div className="aiInsightsReview">
												<p className="aiInsightsMeta">Review this analysis. Approve, or flag a query.</p>
												<Button bsSize="small" bsStyle="success" disabled={busy} onClick={() => submitDelayReview('approved')}>Approve</Button>
												{' '}
												<FormControl
													componentClass="select"
													value={delaySection}
													onChange={e => setDelaySection(e.target.value)}
													style={{ display: 'inline-block', width: 'auto', marginLeft: 8 }}>
													{DELAY_REVIEW_SECTIONS.map(name => (
														<option key={name} value={name}>{name}</option>
													))}
												</FormControl>
												<FormControl
													componentClass="textarea"
													rows={2}
													placeholder="Flag comment"
													value={delayComment}
													onChange={e => setDelayComment(e.target.value)}
												/>
												<FormControl
													componentClass="textarea"
													rows={3}
													placeholder="Optional edited explanation"
													value={delayEdit}
													onChange={e => setDelayEdit(e.target.value)}
												/>
												<Button bsSize="small" disabled={busy} onClick={() => submitDelayReview('flagged')}>Flag</Button>
												{delayReviewNote ? <p className="aiInsightsMeta">{delayReviewNote}</p> : null}
											</div>
										)}
										{delayDebug && delayFacts ? <pre className="aiInsightsDebug">{delayFacts}</pre> : null}
									</div>
								)}
								{delayMode === 'tia' && (
									<div>
										<FormGroup>
											<ControlLabel>Event date</ControlLabel>
											<FormControl type="date" value={tiaEventDate} onChange={e => setTiaEventDate(e.target.value)} />
										</FormGroup>
										<FormGroup>
											<ControlLabel>Scenario name</ControlLabel>
											<FormControl type="text" value={tiaName} placeholder="Access delay" onChange={e => setTiaName(e.target.value)} />
										</FormGroup>
										<FormGroup>
											<ControlLabel>Assumptions</ControlLabel>
											<FormControl
												componentClass="textarea"
												rows={2}
												placeholder="What the fragnet is based on"
												value={tiaAssumptions}
												onChange={e => setTiaAssumptions(e.target.value)}
											/>
										</FormGroup>
										{tiaActivities.map((row, index) => (
											<div key={`fragnet-${index}`}>
												<p className="aiInsightsMeta">Fragnet activity {index + 1}</p>
												<FormGroup>
													<ControlLabel>Activity code</ControlLabel>
													<FormControl type="text" value={row.code} onChange={e => updateFragnet(index, 'code', e.target.value)} />
												</FormGroup>
												<FormGroup>
													<ControlLabel>Name</ControlLabel>
													<FormControl type="text" value={row.name} onChange={e => updateFragnet(index, 'name', e.target.value)} />
												</FormGroup>
												<FormGroup>
													<ControlLabel>Duration (hours)</ControlLabel>
													<FormControl type="number" min="0" value={row.hours} onChange={e => updateFragnet(index, 'hours', e.target.value)} />
												</FormGroup>
												<FormGroup>
													<ControlLabel>Calendar id</ControlLabel>
													<FormControl type="text" placeholder="Project calendar if blank" value={row.calendarId} onChange={e => updateFragnet(index, 'calendarId', e.target.value)} />
												</FormGroup>
												<FormGroup>
													<ControlLabel>Predecessor</ControlLabel>
													<FormControl type="text" placeholder="Existing task code" value={row.pred} onChange={e => updateFragnet(index, 'pred', e.target.value)} />
													<FormControl componentClass="select" value={row.predType} onChange={e => updateFragnet(index, 'predType', e.target.value)}>
														{TIA_REL_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
													</FormControl>
													<FormControl type="number" value={row.predLag} onChange={e => updateFragnet(index, 'predLag', e.target.value)} />
												</FormGroup>
												<FormGroup>
													<ControlLabel>Successor</ControlLabel>
													<FormControl type="text" placeholder="Existing task code" value={row.succ} onChange={e => updateFragnet(index, 'succ', e.target.value)} />
													<FormControl componentClass="select" value={row.succType} onChange={e => updateFragnet(index, 'succType', e.target.value)}>
														{TIA_REL_TYPES.map(type => <option key={`s-${type}`} value={type}>{type}</option>)}
													</FormControl>
													<FormControl type="number" value={row.succLag} onChange={e => updateFragnet(index, 'succLag', e.target.value)} />
												</FormGroup>
												{tiaActivities.length > 1 && (
													<Button bsSize="small" onClick={() => setTiaActivities(prev => prev.filter((_, i) => i !== index))}>Remove</Button>
												)}
											</div>
										))}
										<Button
											bsSize="small"
											disabled={!String((tiaActivities[tiaActivities.length - 1] || {}).code || '').trim()}
											onClick={() => setTiaActivities(prev => prev.concat([blankFragnet()]))}>
											Add another fragnet activity
										</Button>
										<p className="aiInsightsMeta">The activity above is included when you run. Add another only after it has a code.</p>
										<p className="aiInsightsMeta">Optional edit to an existing activity. Applied on the copy only.</p>
										<FormGroup>
											<ControlLabel>Existing activity</ControlLabel>
											<FormControl type="text" value={tiaChangeCode} onChange={e => setTiaChangeCode(e.target.value)} />
										</FormGroup>
										<FormGroup>
											<ControlLabel>New remaining hours</ControlLabel>
											<FormControl type="number" min="0" value={tiaChangeHours} onChange={e => setTiaChangeHours(e.target.value)} />
										</FormGroup>
										<FormGroup>
											<ControlLabel>Constraint</ControlLabel>
											<FormControl componentClass="select" value={tiaChangeConstraint} onChange={e => setTiaChangeConstraint(e.target.value)}>
												<option value="">No constraint change</option>
												<option value="CS_MSOA">Start on or after</option>
												<option value="CS_MEOA">Finish on or after</option>
												<option value="CS_MSO">Start on</option>
												<option value="CS_MEO">Finish on</option>
											</FormControl>
											<FormControl type="date" value={tiaChangeDate} onChange={e => setTiaChangeDate(e.target.value)} />
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input type="checkbox" checked={tiaExplain} onChange={e => setTiaExplain(e.target.checked)} />
												{' '}Explain the table with the local model
											</label>
										</FormGroup>
										<FormGroup>
											<label className="checkbox-inline">
												<input type="checkbox" checked={tiaDebug} onChange={e => setTiaDebug(e.target.checked)} />
												{' '}Debug — show the computed facts
											</label>
										</FormGroup>
										<Button bsStyle="primary" bsSize="small" disabled={busy || !tiaEventDate} onClick={runTia}>
											{busy && tab === 5 ? 'Calculating…' : 'Run time impact'}
										</Button>
										{narrativeProgress && delayMode === 'tia' ? <p className="aiInsightsMeta">{narrativeProgress}</p> : null}
										{tiaParity ? <p className="aiInsightsMeta">{tiaParity}</p> : null}
										{tiaMismatches.length > 0 && (tiaDebug || !tiaSummary) && (
											<div className="aiInsightsDebug">
												<p>First activities whose early dates differ from the imported schedule:</p>
												<ul>
													{tiaMismatches.map(row => (
														<li key={row.code}>
															{row.code}
															{row.name ? ` (${row.name})` : ''}
															{': '}
															{row.earlyStart}
															{' / '}
															{row.earlyFinish}
															{' vs P6 '}
															{row.storedEarlyStart}
															{' / '}
															{row.storedEarlyFinish}
															{row.reason ? `. ${row.reason}` : ''}
														</li>
													))}
												</ul>
											</div>
										)}
										{tiaMethod ? <p className="aiInsightsMeta">{tiaMethod}</p> : null}
										{tiaLimits ? <p className="aiInsightsMeta">{tiaLimits}</p> : null}
										{tiaNote ? <p className="aiInsightsMeta">{tiaNote}</p> : null}
										{tiaWarnings.map(warning => (
											<p key={warning} className="aiInsightsWarn">{warning}</p>
										))}
										{tiaSummary && (
											<p className="aiInsightsMeta">
												{tiaSummary.unimpactedFinish}
												{' → '}
												{tiaSummary.impactedFinish}
												{' ('}
												{tiaSummary.impactCalendarDays}
												{' calendar days, '}
												{tiaSummary.impactWorkHours}
												{' working hours). Zero-duration check '}
												{tiaSummary.zeroDurationMatch ? 'matched.' : 'moved original dates.'}
											</p>
										)}
										{tiaRows.length > 0 && (
											<div className="aiInsightsDelayWrap">
												<table className="aiInsightsDelayTable">
													<thead>
														<tr>
															<th>Item</th>
															<th>Activity</th>
															<th>Unimpacted</th>
															<th>Impacted</th>
															<th>Days</th>
															<th>Detail</th>
															<th>Query</th>
														</tr>
													</thead>
													<tbody>
														{tiaRows.map((row, index) => (
															<tr key={`${row.queryKey}-${row.activity}-${index}`}>
																<td>{row.label}</td>
																<td>{row.activity}</td>
																<td>{row.unimpacted}</td>
																<td>{row.impacted}</td>
																<td>{row.days}</td>
																<td>{row.detail}</td>
																<td>{row.queryKey}@{row.queryVersion}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
										{truncationWarning ? <p className="aiInsightsWarn">{truncationWarning}</p> : null}
										{codeWarning ? <p className="aiInsightsWarn">{codeWarning}</p> : null}
										{tiaText ? <pre className="aiInsightsOutput">{tiaText}</pre> : null}
										{tiaSummary && (
											<div className="aiInsightsReview">
												<Button bsSize="small" disabled={busy} onClick={saveTia}>Save scenario</Button>
												{tiaScenarioNote ? <p className="aiInsightsMeta">{tiaScenarioNote}</p> : null}
												<p className="aiInsightsMeta">Review this analysis. Approve, or flag a query.</p>
												<Button bsSize="small" bsStyle="success" disabled={busy} onClick={() => submitTiaReview('approved')}>Approve</Button>
												{' '}
												<FormControl
													componentClass="select"
													value={tiaSection}
													onChange={e => setTiaSection(e.target.value)}
													style={{ display: 'inline-block', width: 'auto', marginLeft: 8 }}>
													{TIA_REVIEW_SECTIONS.map(name => (
														<option key={name} value={name}>{name}</option>
													))}
												</FormControl>
												<FormControl
													componentClass="textarea"
													rows={2}
													placeholder="Flag comment"
													value={tiaComment}
													onChange={e => setTiaComment(e.target.value)}
												/>
												<FormControl
													componentClass="textarea"
													rows={3}
													placeholder="Optional edited explanation"
													value={tiaEdit}
													onChange={e => setTiaEdit(e.target.value)}
												/>
												<Button bsSize="small" disabled={busy} onClick={() => submitTiaReview('flagged')}>Flag</Button>
												{tiaReviewNote ? <p className="aiInsightsMeta">{tiaReviewNote}</p> : null}
											</div>
										)}
										{tiaDebug && tiaFacts ? <pre className="aiInsightsDebug">{tiaFacts}</pre> : null}
									</div>
								)}
							</Tab>
						</Tabs>

						{error ? <p className="aiInsightsError">{error}</p> : null}
						{flagMessage ? <p className="aiInsightsFlagMsg">{flagMessage}</p> : null}
						{suggested.matched.length > 0 && (tab === 1 || tab === 2)
							? <p className="aiInsightsMeta">Mentioned: {suggested.matched.join(', ')}</p>
							: null}
					</div>
				)
				: null}

			<button
				type="button"
				className="aiInsightsFab"
				aria-label="Open AI Insights"
				onClick={() => setOpen(prev => !prev)}>
				<span className="aiInsightsFabStar" aria-hidden="true">✦</span>
				<span className="aiInsightsFabLabel">ai</span>
			</button>
		</div>
	);
};

AiInsightsPanel.propTypes = {
	datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AiInsightsPanel;
